import { motion } from "framer-motion";
import { User2 } from "lucide-react";
import { resolveMediaUrl } from "@/lib/api";
import { SkeletonTile } from "@/components/Skeleton";
import { useLang } from "@/i18n/LangContext";

export default function Team({ members = [], loading = false }) {
    const { t, lang } = useLang();
    if (!members.length && !loading) return null;

    return (
        <section
            id="zespol"
            data-testid="team-section"
            className="relative py-24 md:py-32 bg-[color:var(--bg-surface)]"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
                    <div className="lg:col-span-6">
                        <div className="label-eyebrow mb-4">{t("07 - Zespół")}</div>
                        <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl tracking-tighter text-white">
                            {lang === "en" ? (
                                t("Ludzie stojący za projektem")
                            ) : (
                                <>Ludzie stojący<br />za projektem</>
                            )}
                        </h2>
                    </div>
                    <p className="lg:col-span-6 text-base text-[color:var(--text-secondary)] max-w-xl">
                        {t("Osoby, które obecnie współtworzą Project Ruda Śląska - od mapowania i modelowania, po testy i wsparcie społeczności.")}
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {loading
                        ? Array.from({ length: 3 }).map((_, i) => (
                              <SkeletonTile key={i} />
                          ))
                        : members.map((m, i) => (
                        <motion.article
                            key={m.id}
                            data-testid={`team-card-${m.id}`}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ delay: (i % 6) * 0.05, duration: 0.45 }}
                            className="panel-yellow p-6 flex flex-col"
                        >
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-14 h-14 rounded-full overflow-hidden bg-[color:var(--bg-base)] border border-[color:var(--border-strong)] flex items-center justify-center shrink-0">
                                    {m.avatar_url ? (
                                        <img
                                            src={resolveMediaUrl(m.avatar_url)}
                                            alt={m.name}
                                            loading="lazy"
                                            decoding="async"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <User2
                                            size={22}
                                            strokeWidth={1.5}
                                            className="text-[color:var(--primary)]"
                                        />
                                    )}
                                </div>
                                <div className="min-w-0">
                                    <div className="font-display uppercase text-lg text-white tracking-tight truncate">
                                        {m.name}
                                    </div>
                                    {m.role && (
                                        <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--primary)] mt-0.5">
                                            {m.role}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {m.description && (
                                <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
                                    {m.description}
                                </p>
                            )}
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
}
